import { useMemo } from "react";

import { create } from "@bufbuild/protobuf";
import {
  ArrowsCounterClockwiseIcon,
  ClockCounterClockwiseIcon,
  PlusIcon,
} from "@phosphor-icons/react";

import FlexWrapper, { FlexDirection } from "@gx/dls/containers/FlexWrapper";
import { SourceSyncStatus } from "@gx/ts-contracts/services/database/models/public_pb";
import { GetSourceUsageRequestSchema } from "@gx/ts-contracts/services/source/api/source_pb";
import { ListSourceSyncsRequestSchema } from "@gx/ts-contracts/services/source/api/source_sync_pb";

import { useEditSourceFormContext } from "@/pages/sources/components/edit/hooks";
import EditSourceOverviewActivityFeed from "@/pages/sources/components/edit/tabs/overview/EditSourceOverviewActivityFeed";
import EditSourceOverviewDetails from "@/pages/sources/components/edit/tabs/overview/EditSourceOverviewDetails";
import EditSourceOverviewMetrics from "@/pages/sources/components/edit/tabs/overview/EditSourceOverviewMetrics";
import type { MockActivityItemType } from "@/pages/sources/components/edit/tabs/overview/types";
import { shouldRefetchSourceSyncs } from "@/pages/sources/components/edit/utils";
import { SYNC_STATUS_TO_TEXT_MAP } from "@/pages/sources/constants";

import { useGetSourceUsageQuery } from "@/api/queries/sources";
import { useListSourceSyncsQuery } from "@/api/queries/syncs";

import { FeatureFlag, useFeatureFlag } from "@/hooks/useFeatureFlag";

import {
  convertProtobufTimestampToDate,
  generateProtobufTimestamp,
  getTimeAgo,
} from "@/utils/datetimes";
import { getUserFullName } from "@/utils/users";
import { formatBytes } from "@/utils/volume";

const EditSourceOverview = () => {
  const { state } = useEditSourceFormContext();

  const isActivityFeedEnabled = useFeatureFlag(FeatureFlag.SOURCE_DETAIL_ACTIVITY_FEED_ENABLED);

  const { data: syncsData } = useListSourceSyncsQuery({
    input: create(ListSourceSyncsRequestSchema, {
      sourceId: state.source.id,
    }),
    options: {
      refetchInterval: (query) => shouldRefetchSourceSyncs(2000, query.state.data?.syncs),
    },
  });

  const latestSyncStatus = useMemo(() => {
    if (!syncsData?.syncs || syncsData.syncs.length === 0) {
      return SourceSyncStatus.UNSPECIFIED;
    }
    return syncsData.syncs[syncsData.syncs.length - 1].syncStatus;
  }, [syncsData?.syncs]);

  const { data: sourceUsage } = useGetSourceUsageQuery({
    input: create(GetSourceUsageRequestSchema, { sourceId: state.source.id }),
  });

  const createdAtDate = useMemo(() => {
    return state.source.createdAt ? convertProtobufTimestampToDate(state.source.createdAt) : null;
  }, [state.source.createdAt]);

  const updatedAtDate = useMemo(() => {
    return state.source.updatedAt ? convertProtobufTimestampToDate(state.source.updatedAt) : null;
  }, [state.source.updatedAt]);

  const createdAtFormatted = useMemo(() => {
    return createdAtDate
      ? createdAtDate.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "—";
  }, [createdAtDate]);

  const updatedAtRelative = useMemo(() => {
    return updatedAtDate ? getTimeAgo(updatedAtDate) : "—";
  }, [updatedAtDate]);

  const createdByAvatarUrl = state.source.createdBy?.avatarUrl ?? null;
  const createdByAvatarHex = state.source.createdBy?.avatarHex;

  const createdByName = useMemo(() => {
    return state.source.createdBy ? getUserFullName(state.source.createdBy) : "—";
  }, [state.source.createdBy]);

  const syncCount = useMemo(() => {
    return (state.source.metrics?.syncCount ?? 0n).toLocaleString();
  }, [state.source.metrics?.syncCount]);

  const projectCount = useMemo(() => {
    return (state.source.metrics?.projectCount ?? 0n).toLocaleString();
  }, [state.source.metrics?.projectCount]);

  const activityItems = useMemo<MockActivityItemType[]>(() => {
    const items: MockActivityItemType[] = [];

    if (state.source.updatedAt) {
      items.push({
        id: "source-status",
        icon: ArrowsCounterClockwiseIcon,
        title: `Sync status is ${SYNC_STATUS_TO_TEXT_MAP[latestSyncStatus]}`,
        detail: "Source runtime state changed",
        createdAt: state.source.updatedAt,
      });

      items.push({
        id: "source-updated",
        icon: ClockCounterClockwiseIcon,
        title: "Source metadata updated",
        detail: "Source record was updated",
        createdAt: state.source.updatedAt,
      });
    }

    if (state.source.createdAt) {
      items.push({
        id: "source-created",
        icon: PlusIcon,
        title: "Source created",
        detail: createdByName !== "—" ? `Created by ${createdByName}` : "Created",
        createdAt: state.source.createdAt,
      });
    }

    if (items.length === 0) {
      items.push({
        id: "source-empty",
        icon: ClockCounterClockwiseIcon,
        title: "No activity yet",
        detail: "No events recorded",
        createdAt: generateProtobufTimestamp(),
      });
    }

    return items;
  }, [state.source.createdAt, state.source.updatedAt, createdByName, latestSyncStatus]);

  return (
    <FlexWrapper direction={FlexDirection.COLUMN} gap={12} fillWidth fillHeight minHeight={0}>
      <EditSourceOverviewMetrics
        syncStatus={latestSyncStatus}
        syncCount={syncCount}
        projectCount={projectCount}
        totalVolume={formatBytes(sourceUsage?.sourceIngestionUsage?.ingestionBytes ?? 0n)}
      />
      <EditSourceOverviewDetails
        createdByName={createdByName}
        createdByAvatarUrl={createdByAvatarUrl}
        createdByAvatarHex={createdByAvatarHex}
        createdAt={createdAtFormatted}
        updatedAtRelative={updatedAtRelative}
      />
      {isActivityFeedEnabled && <EditSourceOverviewActivityFeed items={activityItems} />}
    </FlexWrapper>
  );
};

export default EditSourceOverview;
